import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  BookOpen, ShoppingCart, Store, Shield, Building2, Truck,
  Download, ChevronRight, ChevronDown, Menu, X, Home,
  Package, Users, BarChart3, Settings, CreditCard, Boxes,
  Tags, ShoppingBag, MapPin, Wallet, Star, FileText,
  CheckCircle2, Info, AlertCircle, Lightbulb, ExternalLink,
  ArrowLeft, Search, Heart, Receipt, Bell, LogIn,
  UserPlus, LayoutDashboard, Globe, Ship, DatabaseBackup, Layers
} from 'lucide-react';

/* ─────────────────────────── PRINT STYLES ─────────────────────────── */
const PRINT_STYLE = `
@media print {
  .ug-sidebar, .ug-topbar, .ug-pdf-btn, .ug-mobile-toggle { display: none !important; }
  .ug-shell { display: block !important; }
  .ug-content { margin: 0 !important; padding: 1cm !important; max-width: 100% !important; }
  .ug-section { page-break-inside: avoid; margin-bottom: 2em; }
  .ug-screenshot { max-width: 100% !important; width: 100% !important; }
  .ug-step-card { border: 1px solid #ccc !important; background: #f9f9f9 !important; }
  a[href]:after { content: " (" attr(href) ")"; font-size: 0.75em; color: #666; }
  h1, h2, h3 { page-break-after: avoid; }
  .ug-hero { background: #1B2D4F !important; color: white !important; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
}
`;

/* ─────────────────────────── DATA ─────────────────────────── */
const USER_TYPES = [
  { id: 'buyer',    label: 'Compradores',        icon: ShoppingCart,  color: '#E07B28', badge: 'Público' },
  { id: 'seller',   label: 'Vendedores TCP',      icon: Store,         color: '#1B2D4F', badge: 'TCP' },
  { id: 'mipyme',   label: 'Mayoristas MiPyMe',   icon: Building2,     color: '#2D6A4F', badge: 'MiPyMe' },
  { id: 'provider', label: 'Proveedores Globales', icon: Globe,        color: '#7B3F9E', badge: 'Proveedor' },
  { id: 'admin',    label: 'SuperAdmin',           icon: Shield,        color: '#C0392B', badge: 'Admin' },
];

const GUIDE_DATA = {
  buyer: {
    title: 'Guía del Comprador',
    description: 'Todo lo que necesitas saber para comprar en Bodegón Mi Barrio: desde buscar productos hasta rastrear tu pedido y acumular puntos de lealtad.',
    sections: [
      {
        id: 'buyer-home',
        title: 'Página de inicio',
        icon: Home,
        screenshot: '/guide/home.jpg',
        description: 'La página de inicio es tu punto de entrada a Bodegón Mi Barrio. Aquí encontrarás las categorías principales, ofertas destacadas y acceso rápido a todas las funciones.',
        steps: [
          { title: 'Explorar categorías', text: 'Haz clic en cualquiera de las tarjetas de categorías (Víveres, Bebidas, Hogar, etc.) para ver todos los productos de esa sección.' },
          { title: 'Buscar productos', text: 'Usa la barra de búsqueda en la parte superior. Escribe el nombre del producto y presiona Enter o el ícono de lupa.' },
          { title: 'Seleccionar ubicación', text: 'Haz clic en "Elegir ubicación" para indicar la provincia y municipio del destinatario en Cuba. Esto activa las ofertas disponibles en esa zona.' },
          { title: 'Acceder a tu cuenta', text: 'Haz clic en "Mi cuenta" en la barra superior para iniciar sesión o registrarte.' },
        ],
        tips: [
          { type: 'info', text: 'Debes seleccionar una ubicación de entrega para ver los precios y disponibilidad real de los productos.' },
          { type: 'tip', text: 'Usa la sección "Tiendas cercanas" para encontrar vendedores cerca de la dirección de entrega en Cuba.' },
        ]
      },
      {
        id: 'buyer-products',
        title: 'Navegar y buscar productos',
        icon: Package,
        screenshot: '/guide/products.jpg',
        description: 'La página de productos muestra todo el catálogo disponible con filtros por categoría. Puedes ordenar, filtrar y buscar para encontrar exactamente lo que necesitas.',
        steps: [
          { title: 'Filtrar por categoría', text: 'En el panel izquierdo, haz clic en cualquier categoría para filtrar los productos mostrados.' },
          { title: 'Ordenar resultados', text: 'Usa el menú "Relevancia" en la esquina superior derecha para ordenar por precio o popularidad.' },
          { title: 'Agregar al carrito', text: 'Haz clic en el botón "Agregar" en la tarjeta del producto para añadirlo a tu carrito de compras.' },
          { title: 'Guardar en favoritos', text: 'Haz clic en el ícono de corazón en la tarjeta del producto para guardarlo en tu lista de deseos.' },
        ],
        tips: [
          { type: 'warning', text: 'Los productos muestran "Configura ubicación" hasta que selecciones una dirección de entrega válida.' },
        ]
      },
      {
        id: 'buyer-product-detail',
        title: 'Detalle del producto',
        icon: Search,
        screenshot: '/guide/product_detail.jpg',
        description: 'La página de detalle muestra toda la información del producto: descripción, precio, disponibilidad, reseñas y tiendas disponibles.',
        steps: [
          { title: 'Ver información completa', text: 'Desplázate hacia abajo para leer la descripción completa, especificaciones y presentación del producto.' },
          { title: 'Seleccionar cantidad', text: 'Ajusta la cantidad deseada antes de agregar al carrito.' },
          { title: 'Consultar tiendas', text: 'Si hay múltiples tiendas con este producto, verás las opciones disponibles con sus precios y condiciones.' },
          { title: 'Leer reseñas', text: 'Desplázate hasta la sección de reseñas para ver las opiniones de otros compradores.' },
        ],
        tips: [
          { type: 'info', text: 'La disponibilidad y precio final dependen de la ubicación de entrega seleccionada.' },
        ]
      },
      {
        id: 'buyer-cart',
        title: 'Carrito de compras',
        icon: ShoppingCart,
        screenshot: '/guide/cart.jpg',
        description: 'El carrito de compras muestra todos los productos que has seleccionado. Aquí puedes ajustar cantidades, eliminar productos y ver el resumen del pedido.',
        steps: [
          { title: 'Revisar productos', text: 'Verifica que los productos, cantidades y precios sean correctos.' },
          { title: 'Modificar cantidades', text: 'Usa los botones + y - para ajustar la cantidad de cada producto.' },
          { title: 'Eliminar productos', text: 'Haz clic en el ícono de papelera para eliminar un producto del carrito.' },
          { title: 'Aplicar puntos (BarrioPuntos)', text: 'Si tienes puntos acumulados, puedes usarlos en el resumen del pedido para obtener un descuento.' },
          { title: 'Proceder al pago', text: 'Haz clic en "Proceder al pago" para continuar con el proceso de checkout.' },
        ],
        tips: [
          { type: 'tip', text: 'Los BarrioPuntos pueden descontar hasta el 50% del subtotal. 1 punto = $0.01 USD de descuento.' },
        ]
      },
      {
        id: 'buyer-login',
        title: 'Registro e inicio de sesión',
        icon: LogIn,
        screenshot: '/guide/login.jpg',
        description: 'Para realizar compras necesitas una cuenta. El proceso de registro es rápido y puedes usar tu cuenta de Google.',
        steps: [
          { title: 'Registrarse', text: 'Haz clic en "Mi cuenta" → "Registrarse". Completa el formulario con tu nombre, email y contraseña.' },
          { title: 'Iniciar sesión con Google', text: 'Haz clic en "Continuar con Google" para acceder más rápido con tu cuenta Google existente.' },
          { title: 'Recuperar contraseña', text: 'Haz clic en "¿Olvidaste tu contraseña?" e ingresa tu email para recibir un enlace de recuperación.' },
          { title: 'Activar MFA', text: 'Para mayor seguridad, activa la autenticación de dos factores en "Mi cuenta" → "Seguridad".' },
        ],
        tips: [
          { type: 'info', text: 'La contraseña debe tener al menos 8 caracteres, incluyendo mayúsculas, minúsculas y números.' },
        ]
      },
      {
        id: 'buyer-track',
        title: 'Rastrear pedido',
        icon: Truck,
        screenshot: '/guide/track_order.jpg',
        description: 'Puedes rastrear el estado de tu pedido sin necesidad de iniciar sesión, solo con el número de pedido.',
        steps: [
          { title: 'Acceder al rastreador', text: 'Ve a "Rastrear pedido" en el menú principal o en la barra superior.' },
          { title: 'Ingresar número de pedido', text: 'Escribe el número de pedido que recibiste en tu correo de confirmación.' },
          { title: 'Ver el estado', text: 'Verás el estado actual del pedido y el historial de eventos (confirmado, en proceso, entregado).' },
        ],
        tips: [
          { type: 'info', text: 'El número de pedido lo encuentras en el email de confirmación o en "Mis pedidos" si estás autenticado.' },
        ]
      },
      {
        id: 'buyer-loyalty',
        title: 'Barrio Puntos (Lealtad)',
        icon: Wallet,
        screenshot: '/guide/mi_lealtad.jpg',
        description: 'BarrioPuntos es el programa de lealtad de Bodegón Mi Barrio. Gana puntos con cada compra y canjéalos por descuentos.',
        steps: [
          { title: 'Ver tu saldo', text: 'Ve a "Barrio Puntos" en el menú principal para ver tu saldo actual y su equivalencia en USD.' },
          { title: 'Cómo se ganan los puntos', text: 'Ganas 1 punto por cada $1 USD en mercancía pagada. Los puntos se acreditan cuando el pedido está entregado y el pago externo está registrado.' },
          { title: 'Canjear puntos', text: 'En el carrito de compras, activa "Usar BarrioPuntos" y selecciona cuántos puntos quieres aplicar al pedido.' },
          { title: 'Historial', text: 'Consulta el historial de movimientos (ganados, redimidos, reversos) en la pestaña "Historial".' },
        ],
        tips: [
          { type: 'info', text: '1 punto = $0.01 USD. Puedes descontar hasta el 50% del subtotal por pedido. Los puntos no vencen.' },
          { type: 'tip', text: 'El descuento por puntos es financiado por la plataforma, no por el vendedor.' },
        ]
      },
      {
        id: 'buyer-nearby',
        title: 'Tiendas cercanas',
        icon: MapPin,
        screenshot: '/guide/nearby_stores.jpg',
        description: 'Encuentra tiendas que entregan en la ubicación de tu familiar en Cuba. Ve la distancia, catálogo y condiciones de entrega.',
        steps: [
          { title: 'Seleccionar ubicación', text: 'Elige la provincia, municipio y barrio de la ubicación de entrega.' },
          { title: 'Ver en el mapa', text: 'Las tiendas disponibles aparecen en el mapa y en la lista lateral con su distancia.' },
          { title: 'Ver productos de una tienda', text: 'Haz clic en "Ver productos" en la tarjeta de cualquier tienda para explorar su catálogo.' },
          { title: 'Usar GPS (opcional)', text: 'Si quieres usar tu ubicación actual como referencia, haz clic en "Usar mi ubicación (GPS)".' },
        ],
        tips: [
          { type: 'info', text: 'La distancia mostrada es distancia en línea recta, no ruta. El tiempo de entrega es el configurado por el vendedor.' },
        ]
      },
      {
        id: 'buyer-howto',
        title: 'Cómo funciona',
        icon: Lightbulb,
        screenshot: '/guide/como_funciona.jpg',
        description: 'La página "Cómo funciona" explica el proceso completo de compra, desde elegir productos hasta la entrega en Cuba.',
        steps: [
          { title: 'Proceso de compra', text: 'Sigue los 4 pasos: Elige productos → Selecciona ubicación → Confirma y paga → Tu familia recibe.' },
          { title: 'Métodos de pago', text: 'Los pagos son externos y manuales. El vendedor confirma la recepción del pago antes de preparar el pedido.' },
          { title: 'Cobertura', text: 'Consulta qué provincias y municipios tienen cobertura de entrega activa.' },
        ],
        tips: [
          { type: 'info', text: 'La plataforma no procesa pagos directamente. Los acuerdos de pago son entre el comprador y el vendedor.' },
        ]
      },
    ]
  },

  seller: {
    title: 'Guía del Vendedor TCP',
    description: 'Manual completo para tiendas TCP (Tienda de Comercio Particular). Aprende a registrarte, gestionar tu catálogo, inventario, pedidos y más en Seller Central.',
    sections: [
      {
        id: 'seller-login',
        title: 'Acceso al Seller Portal',
        icon: LogIn,
        screenshot: '/guide/seller_login.jpg',
        description: 'Accede al portal de vendedores desde la opción "Seller Portal" en la tienda pública o directamente en /seller-login.',
        steps: [
          { title: 'Abrir el portal', text: 'Haz clic en "Seller Portal" en la barra superior de la tienda pública, o ve directamente a /seller-login.' },
          { title: 'Seleccionar tipo de cuenta', text: 'Elige "TCP · Tienda simple" para cuentas de tienda minorista.' },
          { title: 'Iniciar sesión', text: 'Ingresa tu email y contraseña registrados. También puedes usar Google si tu cuenta está vinculada.' },
          { title: 'Autenticación de dos factores (MFA)', text: 'Si tienes MFA activado, ingresa el código de tu aplicación autenticadora (Google Authenticator, Authy, etc.).' },
          { title: 'Registrar nueva cuenta', text: 'Si eres nuevo vendedor, haz clic en "Crear cuenta" y completa el proceso de registro.' },
        ],
        tips: [
          { type: 'info', text: 'El acceso al portal requiere aprobación previa de la plataforma para nuevas cuentas.' },
          { type: 'tip', text: 'Activa MFA en tu cuenta para mayor seguridad. Ve a Mi cuenta → Seguridad → Activar MFA.' },
        ]
      },
      {
        id: 'seller-dashboard',
        title: 'Panel de control (Dashboard)',
        icon: LayoutDashboard,
        screenshot: '/guide/home.jpg',
        description: 'El dashboard de Seller Central muestra un resumen operativo de tu tienda: ventas, pedidos abiertos, inventario y pasos de configuración inicial.',
        steps: [
          { title: 'KPIs principales', text: 'Al ingresar verás 4 indicadores clave: Valor de pedidos hoy, Pedidos abiertos, Unidades disponibles y Ofertas activas.' },
          { title: 'Pasos de configuración', text: 'Si es tu primera vez, el panel muestra los 3 pasos para activar tu tienda: Almacenes → Inventario → Tarifas.' },
          { title: 'Cambiar contexto', text: 'Si tienes múltiples tiendas u organizaciones, usa el selector "ORGANIZACIÓN / TIENDA" en la barra superior.' },
          { title: 'Actividad reciente', text: 'La sección inferior muestra el registro de actividad del contexto seleccionado.' },
          { title: 'Búsqueda global', text: 'Usa la barra de búsqueda en la parte superior para buscar SKUs, productos, pedidos o clientes.' },
        ],
        tips: [
          { type: 'info', text: 'Los datos mostrados pertenecen al contexto seleccionado (organización o tienda específica).' },
          { type: 'tip', text: 'Usa el modo oscuro con el ícono de luna/sol en la barra superior.' },
        ]
      },
      {
        id: 'seller-catalog',
        title: 'Catálogo global (Productos)',
        icon: Package,
        screenshot: '/guide/products.jpg',
        description: 'El catálogo global contiene las fichas técnicas de todos los productos. Cada tienda publica sus propias ofertas, precios y stock a partir de estas fichas.',
        steps: [
          { title: 'Ver catálogo', text: 'Ve a Catálogo → "Añadir un producto" en el menú lateral. Aquí verás todos los productos disponibles en la plataforma.' },
          { title: 'Añadir producto al catálogo', text: 'Haz clic en "Añadir producto" para crear una nueva ficha. Completa: nombre, descripción, categoría, peso y unidad.' },
          { title: 'Crear oferta desde catálogo', text: 'En la fila de un producto, haz clic en "Crear oferta" para publicar tu propia oferta con precio y stock.' },
          { title: 'Carga masiva', text: 'Para cargar múltiples productos, usa "Carga masiva por archivo" en el menú Catálogo.' },
          { title: 'Imágenes del producto', text: 'Agrega imágenes a tus productos desde el editor. Las imágenes mejoran la visibilidad y conversión.' },
        ],
        tips: [
          { type: 'info', text: 'El catálogo es compartido. Si el producto ya existe, crea solo la oferta (precio y stock) sin duplicar la ficha.' },
          { type: 'tip', text: 'El SKU global es asignado por la plataforma. Tu SKU de vendedor se genera automáticamente.' },
        ]
      },
      {
        id: 'seller-inventory',
        title: 'Gestión de inventario',
        icon: Boxes,
        screenshot: '/guide/products.jpg',
        description: 'Controla el stock físico de tu tienda. Registra entradas, salidas, ajustes y realiza conteos físicos periódicos.',
        steps: [
          { title: 'Ver inventario', text: 'Ve a Inventario → "Gestión de inventario" para ver todas tus ofertas con su stock disponible, reservado y físico.' },
          { title: 'Ajustar stock', text: 'Haz clic en una oferta y selecciona "Ajustar stock". Ingresa la cantidad y el motivo del ajuste (conteo, entrada, pérdida, etc.).' },
          { title: 'Inventario multidepósito', text: 'Ve a "Inventario multidepósito" para gestionar el stock por almacén. Verás movimientos, conteos y alertas.' },
          { title: 'Escanear código de barras', text: 'Usa el escáner de cámara en el inventario para buscar productos por código de barras o QR.' },
          { title: 'Recibir mercancía', text: 'Usa "Recibir en almacén" para registrar entradas de stock cuando llega nueva mercancía.' },
        ],
        tips: [
          { type: 'warning', text: 'El stock disponible al público se calcula en tiempo real: físico menos reservado.' },
          { type: 'tip', text: 'Activa alertas de bajo stock desde la sección de alertas del inventario multidepósito.' },
        ]
      },
      {
        id: 'seller-listings',
        title: 'Ofertas e inventario de tienda',
        icon: Tags,
        screenshot: '/guide/products.jpg',
        description: 'Las ofertas son la unidad publicable de tu tienda. Cada oferta combina una ficha de catálogo con tu precio, stock y configuración.',
        steps: [
          { title: 'Ver ofertas', text: 'Ve a Inventario → "Gestión de inventario" para ver el listado de tus ofertas.' },
          { title: 'Crear oferta', text: 'Haz clic en "Añadir oferta" y selecciona el producto del catálogo global. Configura precio, stock y estado.' },
          { title: 'Estados de oferta', text: 'Las ofertas pueden estar: ACTIVE (visible al público), DRAFT (borrador, no visible), INACTIVE (desactivada), SUPPRESSED (suspendida por la plataforma).' },
          { title: 'Editar precio', text: 'Usa el ícono de precios (etiqueta) en la fila de la oferta para editar precio B2C y escalas mayoristas B2B.' },
          { title: 'Completar borradores', text: 'Ve a Catálogo → "Completar borradores" para ver y publicar tus ofertas pendientes.' },
        ],
        tips: [
          { type: 'info', text: 'Solo las ofertas ACTIVE con stock > 0 y ubicación configurada son visibles para compradores.' },
        ]
      },
      {
        id: 'seller-pricing',
        title: 'Gestión de precios',
        icon: CreditCard,
        screenshot: '/guide/products.jpg',
        description: 'Configura precios B2C (público), precios B2B (mayorista) y escalas de volumen para tus ofertas.',
        steps: [
          { title: 'Acceder a precios', text: 'Ve a Precios → "Gestión de precios" o usa el ícono de precios en cada oferta.' },
          { title: 'Precio B2C', text: 'Ingresa el precio regular y precio de oferta (sale price) para compradores del público.' },
          { title: 'Precio B2B / Mayorista', text: 'Activa "Habilitado para mayoristas" y configura escalas de precio por volumen (ej: 10+ unidades = $X).' },
          { title: 'Automatización de precios', text: 'Ve a "Precios automáticos" para configurar reglas dinámicas de actualización de precios.' },
          { title: 'Salud de precios', text: 'Consulta "Salud de precios" para detectar ofertas con precios anómalos o inconsistencias.' },
        ],
        tips: [
          { type: 'info', text: 'Los precios se configuran en USD. Los precios B2B son solo visibles para compradores mayoristas habilitados.' },
        ]
      },
      {
        id: 'seller-warehouses',
        title: 'Almacenes y cobertura',
        icon: MapPin,
        screenshot: '/guide/nearby_stores.jpg',
        description: 'Configura tus almacenes con dirección, coordenadas y área de cobertura. Esto determina en qué zonas de Cuba puedes entregar.',
        steps: [
          { title: 'Añadir almacén', text: 'Ve a Configuración → "Almacenes y cobertura". Haz clic en "Añadir almacén" e ingresa nombre y ubicación.' },
          { title: 'Configurar coordenadas', text: 'Ingresa las coordenadas GPS del almacén (latitud/longitud) para cálculo de distancias.' },
          { title: 'Definir cobertura', text: 'Elige entre cobertura por radio (kilómetros) o por polígono personalizado.' },
          { title: 'Tarifas de envío', text: 'Ve a "Tarifario de envíos locales" para configurar precio base, precio por km y ETA de entrega.' },
        ],
        tips: [
          { type: 'tip', text: 'Activa "Esta tienda realiza sus propias entregas" en Configuración para habilitar las tarifas de envío.' },
        ]
      },
      {
        id: 'seller-orders',
        title: 'Gestión de pedidos',
        icon: ShoppingBag,
        screenshot: '/guide/cart.jpg',
        description: 'Administra todos los pedidos de tu tienda: nuevos, en proceso, entregados y cancelados.',
        steps: [
          { title: 'Ver pedidos', text: 'Ve a Pedidos → "Gestión de pedidos" para ver el listado completo de pedidos.' },
          { title: 'Estados del pedido', text: 'Los pedidos pasan por: Nuevo → Confirmado → Preparando → En camino → Entregado.' },
          { title: 'Confirmar pago externo', text: 'Cuando recibas el pago del cliente, marca el pedido como "Pago confirmado" para continuar el proceso.' },
          { title: 'Registrar entrega', text: 'Una vez entregado, cambia el estado a "Entregado". Esto acredita los BarrioPuntos al comprador.' },
          { title: 'Asignar repartidor', text: 'Si tienes flotilla propia, asigna el pedido a un repartidor desde la gestión del pedido.' },
        ],
        tips: [
          { type: 'warning', text: 'Los BarrioPuntos del comprador se acreditan SOLO cuando el pedido está "Entregado" y el pago externo está registrado.' },
        ]
      },
      {
        id: 'seller-golive',
        title: 'Apertura guiada (Go-Live)',
        icon: CheckCircle2,
        screenshot: '/guide/home.jpg',
        description: 'La apertura guiada es un asistente paso a paso para activar tu tienda y publicar tu primera oferta al público.',
        steps: [
          { title: 'Acceder a Go-Live', text: 'Ve a Catálogo → "Apertura guiada" en el menú lateral.' },
          { title: 'Completar pasos', text: 'El asistente te guía por: Perfil empresa → Almacén → Producto → Oferta → Revisión → Activación.' },
          { title: 'Verificar requisitos', text: 'Cada paso muestra el estado de completitud. Completa todos los pasos marcados como pendientes.' },
          { title: 'Publicar tienda', text: 'Una vez completados todos los pasos, tu tienda estará activa y visible para compradores.' },
        ],
        tips: [
          { type: 'info', text: 'La apertura guiada requiere aprobación de la plataforma antes de que tus ofertas sean visibles al público.' },
        ]
      },
      {
        id: 'seller-team',
        title: 'Equipo y permisos',
        icon: Users,
        screenshot: '/guide/home.jpg',
        description: 'Agrega colaboradores a tu tienda con diferentes roles y niveles de acceso.',
        steps: [
          { title: 'Ver equipo', text: 'Ve a "Equipo y personal" → "Usuarios y permisos" en el menú lateral.' },
          { title: 'Añadir miembro', text: 'Haz clic en "Añadir miembro" e ingresa el email de la cuenta que deseas agregar.' },
          { title: 'Asignar rol', text: 'Selecciona el rol: STAFF (acceso operativo), VIEWER (solo lectura) u otros roles disponibles.' },
          { title: 'Repartidores', text: 'Ve a "Flotilla de delivery" para gestionar los repartidores asignados a tu tienda.' },
        ],
        tips: [
          { type: 'info', text: 'La persona debe tener cuenta registrada previamente para ser añadida al equipo.' },
          { type: 'warning', text: 'Los permisos de equipo son por tienda. No conceden acceso a otras tiendas ni a la administración de la plataforma.' },
        ]
      },
      {
        id: 'seller-reports',
        title: 'Informes y conciliación',
        icon: BarChart3,
        screenshot: '/guide/home.jpg',
        description: 'Accede a informes financieros, de inventario y de rendimiento de tu tienda.',
        steps: [
          { title: 'Informes empresariales', text: 'Ve a Informes → "Informes empresariales" para ver resúmenes de ventas, pedidos y rendimiento.' },
          { title: 'Pagos y liquidaciones', text: 'En "Pagos y liquidaciones" verás el detalle de tus liquidaciones y el desglose de BarrioPuntos.' },
          { title: 'Conciliación por archivo', text: 'Sube archivos de conciliación bancaria o de pagos externos para cruzar con los pedidos.' },
          { title: 'Inventario', text: 'En "Rendimiento del inventario" verás métricas de rotación, stock bajo y movimientos históricos.' },
          { title: 'Registro de actividad', text: 'En "Registro de actividad" (parte inferior del menú) verás todos los cambios realizados en tu cuenta con fecha y responsable.' },
        ],
        tips: [
          { type: 'info', text: 'Los pagos registrados son externos. La plataforma no realiza transferencias automáticas.' },
        ]
      },
      {
        id: 'seller-barcodes',
        title: 'Códigos de barras y etiquetas',
        icon: FileText,
        screenshot: '/guide/products.jpg',
        description: 'Genera e imprime etiquetas con código de barras o QR para tus productos. Facilita la gestión física del inventario.',
        steps: [
          { title: 'Generar código de barras', text: 'En el inventario, selecciona un producto y haz clic en el ícono de código de barras para ver el código Code128.' },
          { title: 'Imprimir etiqueta', text: 'Usa "Imprimir etiqueta" para abrir el diálogo de impresión con el código de barras y datos del producto.' },
          { title: 'Impresión masiva', text: 'Selecciona múltiples productos y usa "Imprimir hoja de etiquetas" para imprimir varias etiquetas en una hoja.' },
          { title: 'Escanear al recibir', text: 'Usa el escáner de cámara para escanear productos al recibirlos en almacén y actualizar el stock automáticamente.' },
          { title: 'Escanear para ajustar', text: 'Escanea un código de barras en la pantalla de inventario para abrir directamente el ajuste de stock de ese producto.' },
        ],
        tips: [
          { type: 'tip', text: 'Imprime etiquetas compactas para estantería activando la opción "Etiqueta compacta" en el diálogo de impresión.' },
        ]
      },
      {
        id: 'seller-subscription',
        title: 'Plan de suscripción',
        icon: Receipt,
        screenshot: '/guide/home.jpg',
        description: 'Consulta el plan de suscripción asignado a tu organización y las capacidades incluidas.',
        steps: [
          { title: 'Ver plan', text: 'Ve a Rendimiento → "Plan de suscripción" para ver el plan actual, versión y fecha de vigencia.' },
          { title: 'Capacidades', text: 'La sección "Capacidades" lista las funciones habilitadas en tu plan actual.' },
          { title: 'Historial de cambios', text: 'El historial muestra los cambios de plan anteriores con fecha y asignador.' },
        ],
        tips: [
          { type: 'info', text: 'Este módulo no realiza cobros. Si tienes dudas sobre tu plan, contacta al soporte de la plataforma.' },
        ]
      },
    ]
  },

  mipyme: {
    title: 'Guía del Mayorista MiPyMe',
    description: 'Manual para organizaciones MiPyMe (mayoristas). Incluye funciones de venta mayorista B2B, gestión de múltiples tiendas, marketplace B2B y procurement.',
    sections: [
      {
        id: 'mipyme-access',
        title: 'Acceso y registro MiPyMe',
        icon: LogIn,
        screenshot: '/guide/seller_login.jpg',
        description: 'Las cuentas MiPyMe tienen acceso a funciones mayoristas adicionales. El registro requiere aprobación de la plataforma.',
        steps: [
          { title: 'Registro', text: 'En /seller-login, selecciona "MiPyMe · Mayorista". Completa el formulario con datos de tu empresa.' },
          { title: 'Documentación', text: 'Adjunta los documentos requeridos durante el proceso de registro.' },
          { title: 'Aprobación', text: 'La plataforma revisará tu solicitud. Recibirás un correo cuando tu cuenta esté aprobada.' },
          { title: 'Acceso matriz/tienda', text: 'MiPyMe puede tener una organización matriz y múltiples tiendas. Usa el selector de contexto para cambiar entre ellas.' },
        ],
        tips: [
          { type: 'info', text: 'La cuenta MiPyMe permite gestionar múltiples puntos de venta bajo una misma organización.' },
        ]
      },
      {
        id: 'mipyme-wholesale',
        title: 'Ventas mayoristas B2B',
        icon: Building2,
        screenshot: '/guide/products.jpg',
        description: 'Configura precios mayoristas con escalas de volumen para vender a otros negocios en la plataforma.',
        steps: [
          { title: 'Habilitar oferta para mayoristas', text: 'En el editor de oferta, activa "Habilitado para mayoristas (B2B)".' },
          { title: 'Configurar escalas', text: 'Agrega escalas de precio: define cantidad mínima y precio por unidad para cada escala (ej: 10+ = $X, 50+ = $Y).' },
          { title: 'Visibilidad B2B', text: 'Las ofertas mayoristas solo son visibles para compradores B2B habilitados (otros vendedores y mayoristas de la plataforma).' },
          { title: 'Marketplace B2B', text: 'Ve a "Marketplace B2B" para ver y gestionar tus ofertas y pedidos en el mercado interno.' },
        ],
        tips: [
          { type: 'info', text: 'Los precios B2B no son visibles para compradores del público general.' },
        ]
      },
      {
        id: 'mipyme-procurement',
        title: 'Abastecimiento B2B / Sourcing',
        icon: Layers,
        screenshot: '/guide/home.jpg',
        description: 'Gestiona las compras a proveedores y mayoristas a través de la plataforma.',
        steps: [
          { title: 'Acceder a sourcing', text: 'Ve a Inventario → "Abastecimiento B2B / proveedores".' },
          { title: 'Buscar proveedores', text: 'Busca por producto o categoría para encontrar proveedores disponibles en la plataforma.' },
          { title: 'Realizar pedido', text: 'Selecciona el proveedor y la cantidad. Los pedidos B2B se gestionan externamente y se confirman manualmente.' },
          { title: 'Reabastecimiento', text: 'Usa "Planificación de reabastecimiento" para revisar qué productos están próximos a agotarse y planificar órdenes.' },
        ],
        tips: [
          { type: 'info', text: 'Los pagos B2B son externos y manuales. La plataforma no procesa transferencias entre empresas.' },
        ]
      },
      {
        id: 'mipyme-marketplace',
        title: 'Marketplace B2B interno',
        icon: Store,
        screenshot: '/guide/home.jpg',
        description: 'Accede al marketplace interno para ver ofertas de proveedores globales, comparar precios y realizar pedidos mayoristas.',
        steps: [
          { title: 'Ver marketplace', text: 'Ve a Catálogo → "Marketplace B2B" en el menú lateral.' },
          { title: 'Filtrar ofertas', text: 'Usa los filtros de tipo (Mayorista, Contenedor) y las pestañas Todas/Comparar/Favoritos.' },
          { title: 'Comparar precios', text: 'La vista "Comparar" agrupa el mismo producto de diferentes proveedores para comparación directa.' },
          { title: 'Guardar favoritos', text: 'Haz clic en la estrella de cualquier oferta para guardarla y acceder rápido desde la pestaña Favoritos.' },
          { title: 'Realizar pedido', text: 'En ofertas mayoristas, haz clic en "Pedir por cantidad" para realizar un pedido con la cantidad deseada.' },
        ],
        tips: [
          { type: 'info', text: 'La disponibilidad de acceso al marketplace B2B depende del tipo de cuenta (MiPyMe/Global Provider).' },
        ]
      },
    ]
  },

  provider: {
    title: 'Guía del Proveedor Global',
    description: 'Manual para Proveedores Globales (importadores). Gestiona contenedores importados, oferta en el marketplace B2B y administra pedidos mayoristas.',
    sections: [
      {
        id: 'provider-access',
        title: 'Acceso y registro',
        icon: LogIn,
        screenshot: '/guide/seller_login.jpg',
        description: 'Los Proveedores Globales tienen un portal especializado para gestionar contenedores e importaciones.',
        steps: [
          { title: 'Registro', text: 'Selecciona "Global Provider · Importador" en /seller-login. Completa los datos de tu empresa importadora.' },
          { title: 'Documentación', text: 'Adjunta licencias y documentos de importación requeridos por la plataforma.' },
          { title: 'Portal especializado', text: 'Al ingresar verás el panel de Proveedor Global con módulos de contenedores, marketplace y pedidos.' },
        ],
        tips: [
          { type: 'info', text: 'Los Proveedores Globales no tienen acceso a las funciones de tienda TCP. Su portal es diferente.' },
        ]
      },
      {
        id: 'provider-containers',
        title: 'Gestión de contenedores',
        icon: Ship,
        screenshot: '/guide/home.jpg',
        description: 'Registra y gestiona tus contenedores importados con estado, disponibilidad y precio por unidad.',
        steps: [
          { title: 'Ver contenedores', text: 'Ve a "Proveedor global" → "Contenedores importados" en el menú lateral.' },
          { title: 'Añadir contenedor', text: 'Registra un nuevo contenedor con: producto, cantidad, precio, moneda, puerto de origen y estado.' },
          { title: 'Estados del contenedor', text: 'Los estados son: EN TRÁNSITO → ARRIBADO → DISPONIBLE → AGOTADO.' },
          { title: 'Publicar en marketplace', text: 'Un contenedor con estado DISPONIBLE aparece automáticamente en el marketplace B2B para compradores habilitados.' },
          { title: 'Gestionar pedidos', text: 'Cuando un comprador adquiere unidades de tu contenedor, el pedido aparece en "Pedidos".' },
        ],
        tips: [
          { type: 'info', text: 'La cantidad disponible se reduce automáticamente con cada pedido confirmado.' },
          { type: 'warning', text: 'Al cancelar un pedido de contenedor, la cantidad disponible se repone automáticamente.' },
        ]
      },
      {
        id: 'provider-marketplace',
        title: 'Ofertas en el Marketplace B2B',
        icon: Globe,
        screenshot: '/guide/home.jpg',
        description: 'Tus contenedores disponibles aparecen en el marketplace B2B como ofertas para compradores mayoristas.',
        steps: [
          { title: 'Visibilidad', text: 'Solo compradores con cuenta MiPyMe o Global Provider pueden ver y comprar tus ofertas.' },
          { title: 'PDF de pedidos', text: 'Descarga el PDF con membrete de cada pedido para documentación y entrega.' },
          { title: 'Aprobar pedidos', text: 'Cuando llega un pedido con estado "Pendiente de aprobación", revísalo y apruébalo o cancélalo.' },
          { title: 'Marcar entregado', text: 'Cuando el producto es entregado al comprador, cambia el estado a "Entregado".' },
        ],
        tips: [
          { type: 'info', text: 'Los pedidos B2B son externos y manuales. La plataforma no procesa pagos ni transferencias entre empresas.' },
        ]
      },
    ]
  },

  admin: {
    title: 'Guía del SuperAdmin',
    description: 'Manual completo para administradores de la plataforma Bodegón Mi Barrio. Gestiona organizaciones, catálogo, usuarios, lealtad, suscripciones y configuración global.',
    sections: [
      {
        id: 'admin-access',
        title: 'Acceso a la Plataforma',
        icon: Shield,
        screenshot: '/guide/seller_login.jpg',
        description: 'El panel de administración de la plataforma es accesible solo para cuentas SuperAdmin con dominio autorizado.',
        steps: [
          { title: 'Acceder', text: 'Inicia sesión con tu cuenta SuperAdmin (Google con dominio autorizado). Ve a /platform o usa el menú de usuario → "Administración de plataforma".' },
          { title: 'Cambiar a modo Seller', text: 'Usa el botón "Modo Seller" en la barra superior para acceder al portal de vendedores.' },
          { title: 'Ver tienda pública', text: 'Usa el enlace "Tienda pública" en el menú lateral para ver la tienda como un comprador.' },
        ],
        tips: [
          { type: 'warning', text: 'El acceso SuperAdmin se valida con Google y los dominios autorizados. Los accesos de admin antiguos fueron retirados.' },
        ]
      },
      {
        id: 'admin-organizations',
        title: 'Organizaciones y tiendas',
        icon: Building2,
        screenshot: '/guide/home.jpg',
        description: 'Gestiona todas las organizaciones y tiendas registradas en la plataforma.',
        steps: [
          { title: 'Ver organizaciones', text: 'En la pantalla principal de /platform verás el listado de todas las organizaciones.' },
          { title: 'Ver tiendas', text: 'Expande una organización para ver sus tiendas asociadas y su estado.' },
          { title: 'Editar organización', text: 'Haz clic en el ícono de edición para modificar datos de la organización o cambiar su tipo (TCP/MiPyMe/Global Provider).' },
          { title: 'Gestionar accesos', text: 'Desde cada organización puedes revisar los miembros con acceso y sus roles.' },
        ],
        tips: [
          { type: 'info', text: 'Cada organización puede tener múltiples tiendas. El contexto "Matriz" tiene acceso de solo lectura a todas las tiendas de la organización.' },
        ]
      },
      {
        id: 'admin-approvals',
        title: 'Aprobación de vendedores',
        icon: CheckCircle2,
        screenshot: '/guide/home.jpg',
        description: 'Revisa y aprueba las solicitudes de nuevos vendedores que quieren operar en la plataforma.',
        steps: [
          { title: 'Ver solicitudes', text: 'Ve a "Aprobación de vendedores" en el menú lateral. Verás todas las solicitudes pendientes.' },
          { title: 'Revisar documentación', text: 'Haz clic en una solicitud para ver los datos de la empresa, documentos adjuntos e información del solicitante.' },
          { title: 'Aprobar solicitud', text: 'Si todo está correcto, haz clic en "Aprobar". El vendedor recibirá acceso al portal.' },
          { title: 'Rechazar o pedir más info', text: 'Puedes rechazar una solicitud o marcarla para revisión adicional con un comentario.' },
        ],
        tips: [
          { type: 'info', text: 'Los vendedores aprobados pueden comenzar a configurar su tienda y publicar ofertas.' },
        ]
      },
      {
        id: 'admin-catalog',
        title: 'Catálogo global de productos',
        icon: Package,
        screenshot: '/guide/products.jpg',
        description: 'Administra el catálogo global de productos que todos los vendedores pueden usar para crear sus ofertas.',
        steps: [
          { title: 'Ver catálogo', text: 'Ve a "Productos globales" en el menú lateral para ver todos los productos del catálogo.' },
          { title: 'Crear producto', text: 'Haz clic en "Nuevo producto" para agregar una ficha técnica. Completa: nombre, descripción, categoría, SKU global, GTIN.' },
          { title: 'Editar producto', text: 'Haz clic en el ícono de edición para modificar los datos de cualquier producto.' },
          { title: 'Archivar producto', text: 'Archiva productos que ya no están disponibles. Los archivados no aparecen en búsquedas pero se conservan para historial.' },
          { title: 'Imágenes en masa', text: 'Usa la función de "Carga masiva de imágenes" para subir imágenes a múltiples productos simultáneamente.' },
          { title: 'SKU global', text: 'Usa "Backfill de SKU" para asignar automáticamente SKUs globales a productos que no los tienen.' },
        ],
        tips: [
          { type: 'info', text: 'El catálogo global es compartido. Todos los vendedores crean ofertas a partir de estas fichas.' },
        ]
      },
      {
        id: 'admin-categories',
        title: 'Categorías globales',
        icon: Tags,
        screenshot: '/guide/products.jpg',
        description: 'Gestiona las categorías que clasifican los productos del catálogo.',
        steps: [
          { title: 'Ver categorías', text: 'Ve a "Categorías globales" en el menú lateral.' },
          { title: 'Crear categoría', text: 'Haz clic en "Nueva categoría" e ingresa nombre, descripción, ícono y orden de aparición.' },
          { title: 'Editar categoría', text: 'Haz clic en "Editar" para modificar el nombre, descripción o estado de una categoría.' },
          { title: 'Safe delete', text: 'No puedes eliminar categorías con productos activos. Primero reasigna los productos o archiva la categoría.' },
          { title: 'Ordenar', text: 'Modifica el campo "Orden" para controlar el orden de aparición en la tienda pública.' },
        ],
        tips: [
          { type: 'warning', text: 'Las categorías son globales y afectan a toda la plataforma. Cualquier cambio es visible inmediatamente.' },
        ]
      },
      {
        id: 'admin-users',
        title: 'Consola de usuarios',
        icon: Users,
        screenshot: '/guide/home.jpg',
        description: 'Consulta, busca y gestiona todos los usuarios registrados en la plataforma.',
        steps: [
          { title: 'Buscar usuario', text: 'Ve a "Consola de usuarios". Usa la búsqueda por email, nombre o ID.' },
          { title: 'Ver detalles', text: 'Haz clic en un usuario para ver sus datos, membresías en organizaciones y estado de cuenta.' },
          { title: 'Suspender cuenta', text: 'Desde el detalle del usuario puedes suspender o reactivar su acceso a la plataforma.' },
          { title: 'Resetear contraseña', text: 'Envía un email de recuperación de contraseña a cualquier usuario desde la consola.' },
        ],
        tips: [
          { type: 'info', text: 'Los SuperAdmins se identifican por su dominio de email autorizado. No uses la consola para elevar permisos manualmente.' },
        ]
      },
      {
        id: 'admin-loyalty',
        title: 'BarrioPuntos (Lealtad)',
        icon: Wallet,
        screenshot: '/guide/mi_lealtad.jpg',
        description: 'Configura y audita el programa de lealtad BarrioPuntos.',
        steps: [
          { title: 'Ver configuración', text: 'Ve a "Barrio Puntos" para ver las reglas activas del programa.' },
          { title: 'Habilitar/deshabilitar', text: 'Usa el toggle "Habilitar módulo" para activar o desactivar el programa globalmente.' },
          { title: 'Ajuste manual de puntos', text: 'Busca un usuario e ingresa la cantidad de puntos a ajustar con el motivo correspondiente.' },
          { title: 'Auditoría', text: 'La tabla de ajustes manuales registra todos los cambios con fecha, admin y motivo.' },
        ],
        tips: [
          { type: 'warning', text: 'El descuento por puntos es financiado por la plataforma. Nunca trates la contribución de plataforma como efectivo del vendedor.' },
          { type: 'info', text: 'Las reglas son fijas: 1pt/$1 ganado, 1pt=$0.01 redimido, máx 50% subtotal, sin vencimiento, saldo global.' },
        ]
      },
      {
        id: 'admin-subscriptions',
        title: 'Planes de suscripción',
        icon: Receipt,
        screenshot: '/guide/home.jpg',
        description: 'Crea y gestiona planes de suscripción para asignar a organizaciones y vendedores.',
        steps: [
          { title: 'Ver planes', text: 'Ve a "Planes de suscripción" para ver todos los planes activos y archivados.' },
          { title: 'Crear plan', text: 'Haz clic en "Nuevo plan" y completa: nombre, descripción y lista de capacidades del plan.' },
          { title: 'Asignar a organización', text: 'En la fila de un plan, haz clic en "Asignar" y selecciona la organización o vendedor destino.' },
          { title: 'Versiones de plan', text: 'Cada vez que editas un plan se crea una nueva versión. Puedes ver el historial en la pestaña "Historial de versiones".' },
          { title: 'Archivar plan', text: 'Archiva planes en desuso. Los planes archivados conservan su historial pero no permiten nuevas asignaciones.' },
        ],
        tips: [
          { type: 'info', text: 'Los planes son informativos. No se realizan cobros automáticos ni se bloquea el acceso por falta de pago.' },
        ]
      },
      {
        id: 'admin-pricing',
        title: 'Precios de suscripción',
        icon: CreditCard,
        screenshot: '/guide/home.jpg',
        description: 'Configura los precios de los planes de suscripción por tipo de cuenta y periodicidad.',
        steps: [
          { title: 'Acceder', text: 'Ve a "Precios de suscripción" en el menú lateral.' },
          { title: 'Ver precios por tipo', text: 'Los precios se configuran por tipo de cuenta: TCP, MiPyMe, Global Provider.' },
          { title: 'Precios mensuales/anuales', text: 'Configura el precio mensual y el precio anual (con descuento opcional) para cada tipo.' },
          { title: 'Sincronizar con Stripe', text: 'Usa "Sincronización Stripe" para publicar los precios en el sistema de facturación.' },
        ],
        tips: [
          { type: 'info', text: 'Los precios por defecto son $50/mes TCP, $100/mes MiPyMe, $200/mes Global Provider.' },
        ]
      },
      {
        id: 'admin-stripe',
        title: 'Sincronización Stripe',
        icon: CreditCard,
        screenshot: '/guide/home.jpg',
        description: 'Sincroniza productos, precios y clientes del catálogo de la plataforma con Stripe para facturación.',
        steps: [
          { title: 'Ver estado', text: 'Ve a "Sincronización Stripe" para ver el estado de sincronización actual.' },
          { title: 'Sincronizar catálogo', text: 'Haz clic en "Sincronizar catálogo" para sincronizar todos los productos y precios con Stripe.' },
          { title: 'Sincronización individual', text: 'Puedes sincronizar productos o clientes individuales desde la tabla de elementos.' },
          { title: 'Stripe Tax', text: 'La sección de impuestos muestra el estado de Stripe Tax. Configura el modo de impuestos desde el dashboard de Stripe.' },
        ],
        tips: [
          { type: 'warning', text: 'Stripe está configurado en modo sandbox (pruebas). No procesa pagos reales hasta completar la verificación de cuenta en producción.' },
        ]
      },
      {
        id: 'admin-locations',
        title: 'Ubicaciones de Cuba',
        icon: MapPin,
        screenshot: '/guide/nearby_stores.jpg',
        description: 'Gestiona la base de datos de ubicaciones geográficas de Cuba: provincias, municipios y barrios.',
        steps: [
          { title: 'Ver ubicaciones', text: 'Ve a "Ubicaciones de Cuba" para ver la jerarquía geográfica: Provincia → Municipio → Barrio.' },
          { title: 'Añadir ubicación', text: 'Agrega nuevas ubicaciones con nombre, tipo, coordenadas y descripción.' },
          { title: 'Editar en mapa', text: 'Usa el mapa interactivo para ajustar las coordenadas de una ubicación arrastrando el marcador.' },
          { title: 'Cobertura de barrios', text: 'Los barrios tienen cobertura variable. Revisa la etiqueta de procedencia de cada barrio.' },
        ],
        tips: [
          { type: 'info', text: 'Cuba tiene 16 provincias y 168 municipios. Los barrios son suplementarios y pueden no estar completos.' },
        ]
      },
      {
        id: 'admin-support',
        title: 'Bandeja de soporte',
        icon: Bell,
        screenshot: '/guide/contacto.jpg',
        description: 'Gestiona los tickets de soporte enviados por compradores y vendedores a través del formulario de contacto.',
        steps: [
          { title: 'Ver tickets', text: 'Ve a "Bandeja de soporte" para ver todos los mensajes de soporte recibidos.' },
          { title: 'Responder ticket', text: 'Haz clic en un ticket para ver el detalle y responder al usuario.' },
          { title: 'Cambiar estado', text: 'Marca los tickets como Abierto, En proceso o Resuelto según el estado de la atención.' },
        ],
        tips: [
          { type: 'info', text: 'Los mensajes de contacto de la tienda pública llegan aquí. Responde a través del email registrado del usuario.' },
        ]
      },
      {
        id: 'admin-email',
        title: 'Plantillas de correo',
        icon: FileText,
        screenshot: '/guide/home.jpg',
        description: 'Personaliza las plantillas de los correos transaccionales enviados por la plataforma.',
        steps: [
          { title: 'Ver plantillas', text: 'Ve a "Plantillas de correo" para ver todas las plantillas disponibles.' },
          { title: 'Editar plantilla', text: 'Haz clic en una plantilla para editar el asunto, cuerpo y variables dinámicas.' },
          { title: 'Previsualizar', text: 'Usa la vista previa para ver cómo queda el correo antes de guardar.' },
          { title: 'Guardar cambios', text: 'Los cambios se aplican inmediatamente a todos los correos futuros de ese tipo.' },
        ],
        tips: [
          { type: 'info', text: 'Los correos se envían via Resend. Asegúrate de que el dominio de envío esté verificado.' },
        ]
      },
      {
        id: 'admin-backups',
        title: 'Copias de seguridad',
        icon: DatabaseBackup,
        screenshot: '/guide/home.jpg',
        description: 'Gestiona las copias de seguridad de la base de datos de la plataforma.',
        steps: [
          { title: 'Ver backups', text: 'Ve a "Copias de seguridad" para ver el listado de backups disponibles.' },
          { title: 'Crear backup manual', text: 'Haz clic en "Crear backup" para generar una copia de seguridad inmediata.' },
          { title: 'Programar backup', text: 'Configura el backup automático semanal con la fecha y hora deseadas.' },
          { title: 'Previsualizar', text: 'Antes de restaurar, usa "Previsualizar" para ver el contenido del backup.' },
          { title: 'Restaurar', text: 'En casos de emergencia, usa "Restaurar" para recuperar el estado de la base de datos al momento del backup.' },
        ],
        tips: [
          { type: 'warning', text: 'La restauración de backup es irreversible. Siempre crea un backup manual antes de restaurar.' },
          { type: 'info', text: 'Los backups se almacenan en la base de datos con control de versiones.' },
        ]
      },
    ]
  },
};

/* ─────────────────────────── TIP COMPONENTS ─────────────────────────── */
const TIP_STYLES = {
  info:    { bg: 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800', icon: Info,         color: 'text-blue-600 dark:text-blue-400' },
  tip:     { bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800', icon: Lightbulb, color: 'text-green-600 dark:text-green-400' },
  warning: { bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800', icon: AlertCircle, color: 'text-amber-600 dark:text-amber-400' },
};

const TipBox = ({ type = 'info', text }) => {
  const s = TIP_STYLES[type] || TIP_STYLES.info;
  const Icon = s.icon;
  return (
    <div className={`flex gap-2 items-start p-3 rounded-lg border text-sm ${s.bg}`}>
      <Icon className={`mt-0.5 shrink-0 h-4 w-4 ${s.color}`} />
      <span className="text-foreground/80">{text}</span>
    </div>
  );
};

/* ─────────────────────────── SECTION COMPONENT ─────────────────────────── */
const GuideSection = ({ section }) => {
  const Icon = section.icon;
  return (
    <div className="ug-section mb-12" id={section.id} data-testid={`ug-section-${section.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-[#1B2D4F] text-white flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <h2 className="text-xl font-bold text-[#1B2D4F] dark:text-white">{section.title}</h2>
      </div>

      <p className="text-muted-foreground mb-5 leading-relaxed">{section.description}</p>

      {section.screenshot && (
        <div className="ug-screenshot mb-6 rounded-xl overflow-hidden border border-border shadow-md">
          <img
            src={section.screenshot}
            alt={`Captura: ${section.title}`}
            className="w-full h-auto block"
            loading="lazy"
            onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
          />
        </div>
      )}

      {section.steps && section.steps.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Pasos</h3>
          <div className="space-y-3">
            {section.steps.map((step, i) => (
              <div key={i} className="ug-step-card flex gap-3 p-4 rounded-lg bg-card border border-border" data-testid={`ug-step-${section.id}-${i}`}>
                <span className="h-6 w-6 rounded-full bg-[#E07B28] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <div>
                  <p className="font-semibold text-sm text-foreground">{step.title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {section.tips && section.tips.length > 0 && (
        <div className="space-y-2">
          {section.tips.map((tip, i) => <TipBox key={i} {...tip} />)}
        </div>
      )}

      <Separator className="mt-10" />
    </div>
  );
};

/* ─────────────────────────── SIDEBAR ─────────────────────────── */
const GuideSidebar = ({ activeType, activeSection, onTypeChange, onSectionClick, sidebarOpen, onClose }) => {
  const guide = GUIDE_DATA[activeType];
  return (
    <>
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={onClose} />
      )}
      <aside
        className={`ug-sidebar fixed top-0 left-0 h-full z-40 w-72 bg-[#1B2D4F] text-white flex flex-col transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:z-auto`}
        data-testid="ug-sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <img src="/media/logo-bodegon-transparent.png" alt="Bodegón Mi Barrio" className="h-8 w-8" onError={(e) => { e.currentTarget.style.display='none'; }} />
            <div>
              <p className="font-bold text-sm leading-none">Bodegón Mi Barrio</p>
              <p className="text-xs text-white/60">Guía del Usuario</p>
            </div>
          </Link>
          <Button variant="ghost" size="icon" className="absolute top-3 right-3 text-white lg:hidden h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* User type tabs */}
        <div className="p-3 border-b border-white/10">
          <p className="text-xs text-white/50 uppercase tracking-wider mb-2 px-1">Tipo de usuario</p>
          <div className="space-y-1">
            {USER_TYPES.map(({ id, label, icon: Icon, color, badge }) => (
              <button
                key={id}
                onClick={() => { onTypeChange(id); onClose(); }}
                data-testid={`ug-type-${id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left
                  ${activeType === id ? 'bg-white/15 text-white font-semibold' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
              >
                <Icon className="h-4 w-4 shrink-0" style={{ color: activeType === id ? color : undefined }} />
                <span className="flex-1 truncate">{label}</span>
                {activeType === id && (
                  <Badge variant="outline" className="text-[10px] border-white/30 text-white/80 px-1.5 py-0">{badge}</Badge>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Section list */}
        <div className="flex-1 overflow-y-auto p-3">
          <p className="text-xs text-white/50 uppercase tracking-wider mb-2 px-1">Secciones</p>
          <div className="space-y-0.5">
            {guide?.sections.map((sec) => {
              const Icon = sec.icon;
              return (
                <button
                  key={sec.id}
                  onClick={() => {
                    onSectionClick(sec.id);
                    onClose();
                  }}
                  data-testid={`ug-nav-${sec.id}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left
                    ${activeSection === sec.id ? 'bg-[#E07B28] text-white font-semibold' : 'text-white/65 hover:bg-white/10 hover:text-white'}`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{sec.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10">
          <p className="text-[10px] text-white/40 text-center">© Bodegón Mi Barrio · 2024</p>
        </div>
      </aside>
    </>
  );
};

/* ─────────────────────────── MAIN PAGE ─────────────────────────── */
export default function UserGuide() {
  const [activeType, setActiveType] = useState('buyer');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const contentRef = useRef(null);

  const guide = GUIDE_DATA[activeType];
  const userType = USER_TYPES.find((u) => u.id === activeType);

  const handleSectionClick = useCallback((sectionId) => {
    setActiveSection(sectionId);
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  const handleTypeChange = (type) => {
    setActiveType(type);
    setActiveSection('');
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{PRINT_STYLE}</style>
      <div className="ug-shell min-h-screen flex bg-background" data-testid="user-guide-page">

        {/* Sidebar */}
        <GuideSidebar
          activeType={activeType}
          activeSection={activeSection}
          onTypeChange={handleTypeChange}
          onSectionClick={handleSectionClick}
          sidebarOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0 lg:ml-0">

          {/* Topbar */}
          <header className="ug-topbar sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border flex items-center gap-3 px-4 py-3" data-testid="ug-topbar">
            <button
              className="ug-mobile-toggle lg:hidden p-2 rounded-md hover:bg-muted transition-colors"
              onClick={() => setSidebarOpen(true)}
              aria-label="Abrir menú"
              data-testid="ug-mobile-toggle"
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-2 flex-1">
              <BookOpen className="h-5 w-5 text-[#E07B28] shrink-0" />
              <div>
                <h1 className="font-bold text-sm text-foreground leading-none">Guía del Usuario</h1>
                <p className="text-xs text-muted-foreground">Bodegón Mi Barrio</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button asChild variant="ghost" size="sm" className="gap-1.5 text-xs hidden sm:flex" data-testid="ug-back-btn">
                <Link to="/"><ArrowLeft className="h-3.5 w-3.5" /> Tienda</Link>
              </Button>
              <Button
                onClick={handlePrint}
                size="sm"
                className="ug-pdf-btn gap-1.5 text-xs bg-[#1B2D4F] hover:bg-[#1B2D4F]/90 text-white"
                data-testid="ug-export-pdf-btn"
              >
                <Download className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Exportar PDF</span>
                <span className="sm:hidden">PDF</span>
              </Button>
            </div>
          </header>

          {/* Content */}
          <main ref={contentRef} className="ug-content flex-1 overflow-y-auto px-4 sm:px-8 py-8 max-w-4xl mx-auto w-full">

            {/* Hero */}
            <div className="ug-hero bg-[#1B2D4F] text-white rounded-2xl p-6 sm:p-8 mb-10" data-testid="ug-hero">
              <div className="flex items-start gap-4">
                <div
                  className="h-14 w-14 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: userType?.color || '#E07B28' }}
                >
                  {userType && <userType.icon className="h-7 w-7 text-white" />}
                </div>
                <div className="flex-1">
                  <Badge variant="outline" className="border-white/30 text-white/80 text-[10px] mb-2">{userType?.badge}</Badge>
                  <h1 className="text-2xl sm:text-3xl font-bold leading-tight mb-2">{guide?.title}</h1>
                  <p className="text-white/75 leading-relaxed text-sm sm:text-base">{guide?.description}</p>
                </div>
              </div>

              {/* Quick nav pills */}
              <div className="mt-6 flex flex-wrap gap-2">
                {guide?.sections.slice(0, 6).map((sec) => {
                  const Icon = sec.icon;
                  return (
                    <button
                      key={sec.id}
                      onClick={() => handleSectionClick(sec.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-xs text-white/90 transition-colors"
                      data-testid={`ug-hero-nav-${sec.id}`}
                    >
                      <Icon className="h-3 w-3" />
                      {sec.title}
                    </button>
                  );
                })}
                {guide?.sections.length > 6 && (
                  <span className="flex items-center gap-1 px-3 py-1.5 bg-white/5 rounded-full text-xs text-white/50">
                    +{guide.sections.length - 6} más
                  </span>
                )}
              </div>
            </div>

            {/* Sections */}
            {guide?.sections.map((section) => (
              <GuideSection key={section.id} section={section} />
            ))}

            {/* Footer CTA */}
            <div className="mt-8 p-6 bg-card border border-border rounded-xl text-center" data-testid="ug-footer-cta">
              <h3 className="font-bold text-lg mb-2">¿Necesitas más ayuda?</h3>
              <p className="text-muted-foreground text-sm mb-4">Nuestro equipo de soporte está disponible para atenderte.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button asChild className="bg-[#E07B28] hover:bg-[#E07B28]/90 text-white" data-testid="ug-contact-btn">
                  <Link to="/contacto">Contactar soporte</Link>
                </Button>
                <Button asChild variant="outline" data-testid="ug-home-btn">
                  <Link to="/">Ir a la tienda <ExternalLink className="ml-1.5 h-3.5 w-3.5" /></Link>
                </Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
